import { z } from "zod";
import { fetchFastApi } from "../../api/fastapi";
import {
  AnalyticsWorkflowError,
  toAnalyticsWorkflowError,
} from "../../lib/analyticsWorkflow";
import { useHookCacheQuery } from "../../lib/hookCache";
import {
  AnalyticsColumnResult,
  AnalyticsColumnResultSchema,
} from "../../models/audit/analyticsColumnResult";

const analyticsResultDetailEnvelopeSchema = z.object({
  result: AnalyticsColumnResultSchema,
});

/**
 * One analytics verdict by id, its manifest and trail hydrated, lazily fetched
 * for the review pane.
 *
 * Reachable for a superseded verdict as well as the live one: history is
 * addressed by id, and the panel that shows a cell's disagreement needs both.
 *
 * The route is unscoped from the procedure session at the URL level, exactly as
 * its attribute-test sibling is. The session id stays in the cache key anyway so
 * the Generate cascade in `useSessionRunsTransitions` can prefix-invalidate
 * `["procedure-session", psId, "analytics-result"]` and wipe every per-verdict
 * entry for the session in one pass. The query stays disabled until both ids are
 * known, so no entry is ever registered under an empty session segment, which
 * that prefix could never reach.
 *
 * The parse sits outside the request's `catch`, matching `requestAnalytics`: an
 * answered call whose body does not match the schema is its own failure rather
 * than the request's, and a `ZodError` reaching `toAnalyticsWorkflowError` would
 * travel as the error's `detail`. The review pane maps failures to its own copy
 * by kind, so the drifted field and path reach no surface at all — the console
 * is the only place they survive.
 */
export function useAnalyticsResultDetail(
  procedureSessionId: string | undefined,
  analyticsResultId: string | undefined,
) {
  return useHookCacheQuery<AnalyticsColumnResult>({
    key: [
      "procedure-session",
      procedureSessionId ?? "",
      "analytics-result",
      analyticsResultId ?? "",
    ],
    fetcher: async () => {
      let response: unknown;
      try {
        response = await fetchFastApi({
          method: "GET",
          path: `/matching/analytics-results/${analyticsResultId}`,
          throwOnErrorEnvelope: true,
        });
      } catch (error) {
        throw toAnalyticsWorkflowError(error);
      }

      const parsed = analyticsResultDetailEnvelopeSchema.safeParse(response);
      if (!parsed.success) {
        console.error("Unreadable analytics result:", parsed.error.issues);
        // No detail: the review pane renders its own copy by kind and never an
        // error's message, so a sentence here would be a second copy of the
        // pane's that nothing reads and nothing keeps in step.
        throw new AnalyticsWorkflowError("unreadable", 0, null);
      }
      return parsed.data.result;
    },
    enabled: !!procedureSessionId && !!analyticsResultId,
  });
}
